import { displayStageLabel, IQ_ASSESSMENT_LABEL } from '@/lib/stageLabels';
import { fmtDateTimeIST } from '@/lib/format';

export const DURATION_MIN_SECS = 60;
export const DURATION_MAX_SECS = 4 * 3600;
export const TECH_DIFFICULTIES = ['easy', 'medium', 'hard'];

export function matchesTechQuestionFilters(q, categoryFilter, difficultyFilter) {
  if (categoryFilter.length && !categoryFilter.includes(q.topic)) return false;
  if (difficultyFilter.length && !difficultyFilter.includes(q.difficulty)) return false;
  return true;
}

export const STAGE_CATALOGUE = [
  { code: 'RESUME', label: 'Resume', mandatory: true, instances: 'exactly 1', maxInstances: 1 },
  { code: 'VIDEOBOT', label: 'Video Bot', instances: '0 or 1', maxInstances: 1 },
  { code: 'MCQ', label: IQ_ASSESSMENT_LABEL, instances: '0 or 1', maxInstances: 1 },
  { code: 'TECH_ASSESSMENT', label: 'Tech Assessment', instances: '0 or 1', maxInstances: 1 },
  // UI cap of 4; backend allows panel/code_panel to repeat (REPEATABLE_STAGE_CODES).
  { code: 'PANEL', label: 'Panel Interview', instances: '1 to 4', maxInstances: 4 },
  { code: 'OFFER', label: 'Offer', instances: '0 or 1 (last)', maxInstances: 1 },
];

/** Whether catalogue stage `code` can still be added given current `stages`. */
export function canAddStage(code, stages) {
  const entry = STAGE_CATALOGUE.find((s) => s.code === code);
  if (!entry || entry.mandatory) return false;
  const count = (stages || []).filter((s) => s.code === code).length;
  return count < entry.maxInstances;
}

// Sprint 12 — Adapt v1 PipelineOut shape to the legacy UI shape this page
// was originally built against. v1 returns `pipeline_key`/`current_version.stages`
// with lowercase stage codes (`videobot`); the UI expects `key`/`stages` with
// UPPER codes (`VIDEOBOT`). One-shot conversion keeps the rest of the page
// untouched. To-do: refactor the UI to consume the canonical v1 shape.
export function adaptStagesFromV1(stages) {
  return (stages || []).map((s) => ({
    id: s.id || null,
    code: (s.stage_code || '').toUpperCase(),
    label: displayStageLabel((s.stage_code || '').toUpperCase(), s.label),
    code_editor: s.options?.code_editor !== false,
  }));
}

export function adaptPipeline(p) {
  if (!p) return null;
  const cv = p.current_version || {};
  const roleIds = Array.isArray(p.role_ids) && p.role_ids.length
    ? p.role_ids
    : (p.role_id ? [p.role_id] : []);
  return {
    _id: p.id,
    key: p.pipeline_key,
    label: p.label,
    role: roleIds[0] || '',
    roles: roleIds,
    roleNames: Array.isArray(p.role_names) ? p.role_names : [],
    department: p.department_id || '',
    version: cv.version || 1,
    stages: adaptStagesFromV1(cv.stages),
  };
}

export function formatVersionTimestamp(iso) {
  if (!iso) return '—';
  try {
    return fmtDateTimeIST(iso, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }, String(iso));
  } catch {
    return String(iso);
  }
}

export function stageCatalogueLabel(code) {
  return STAGE_CATALOGUE.find((s) => s.code === code)?.label || code;
}

export function formatApiError(detail, fallback = 'Save failed') {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean);
    if (msgs.length) return msgs.join(' ');
  }
  return fallback;
}

export function toV1Stages(stages) {
  return stages.map((s) => ({
    stage_code: (s.code || '').toLowerCase(),
    label: (s.label || '').trim(),
    options: s.code === 'PANEL' || s.code === 'CODE_PANEL'
      ? { code_editor: s.code_editor !== false }
      : undefined,
  }));
}
